import { isUploadFailedError } from '../../api/upload';

/**
 * One retry policy for both queues — mutations and uploads.
 *
 * THE STATE MACHINE THIS REPLACES WAS BROKEN IN BOTH DIRECTIONS AT ONCE
 *
 * Dropped work: `sync-queue` and `upload-sync` both filtered `retryCount < 3`.
 * `getPendingCount()` was the length of that same filtered list, so the moment
 * a mutation hit its third failure it left the flush AND the count together.
 * `SyncStatusBar` then rendered nothing — and the pending bar going away is
 * the app's only "you are caught up" signal. `cleanupQueue` deletes only
 * `applied` and `conflict` rows, so the mutation sat in IndexedDB forever,
 * invisible to everyone including the person whose work it was.
 *
 * Immortal work: the same file's `catch` only reverted to `pending` when
 * `!err.response`, and `retryCount` was only ever incremented from a 200 body.
 * So a 400 never incremented anything — it stayed `pending` and was re-POSTed
 * on every sync cycle, forever, at every reconnect, for the life of the
 * install.
 *
 * Those are the same bug: the code decided what to do next from `retryCount`
 * without ever asking *why* the attempt failed. Three failure kinds are not
 * interchangeable, and only one of them says anything about the work itself.
 *
 * ── network ────────────────────────────────────────────────────────────────
 * No response at all: no radio, no route, a dead zone, a stalled socket. The
 * server never saw the mutation, so nothing about it has been judged. This is
 * NOT COUNTED AS AN ATTEMPT and never exhausts. A hand three days into a
 * shutdown with no coverage must not lose a ticket because a background timer
 * ticked twelve hundred times while they were out of range — there is nothing
 * to give up on. The work stays `pending` and stays in the pending count,
 * which is exactly what the yellow "waiting to sync" bar is for.
 *
 * ── server ─────────────────────────────────────────────────────────────────
 * 5xx, 408, 429. The request arrived and the *server* failed, not the data.
 * Worth retrying with the same bytes, and worth retrying more than a couple of
 * times because deploys and rate limits pass. Bounded anyway, because a
 * mutation that a server rejects five times running needs a person to look at
 * it, not a sixth attempt.
 *
 * ── rejected ───────────────────────────────────────────────────────────────
 * Any other 4xx, and per-mutation errors in a 200 `/sync/push` body. The
 * server read the mutation and said no: malformed payload, failed validation,
 * a foreign key to something deleted, a role that no longer permits it.
 * Byte-identical retries are rejected byte-identically — this is the failure
 * that has to be allowed to STOP. Two attempts, so a single fluke (a token
 * that expired mid-flight and re-signed on the retry) still recovers, and then
 * it is done retrying and becomes the user's problem to see, not the sync
 * loop's problem to repeat.
 *
 * Exhausted never means deleted. It means `failed`, which is a status the UI
 * renders — see `getFailedMutations`/`getFailedUploads` and the red bar in
 * `SyncStatusBar`.
 */

export type SyncFailureKind = 'network' | 'server' | 'rejected';

/** A server that answered badly gets more rope than one that said no. */
export const MAX_SERVER_ERROR_ATTEMPTS = 5;

/** One retry past the first rejection, then stop. */
export const MAX_REJECTED_ATTEMPTS = 2;

/** Statuses that transient-retry, transported through IndexedDB. */
export type QueueStatus = 'pending' | 'error' | 'failed';

/**
 * Classify a failure from `apiClient` (axios) or from `putToPresignedUrl`.
 */
export function classifyFailure(err: any): SyncFailureKind {
  // Presigned PUT — already classified at the source, where the Response was.
  if (isUploadFailedError(err)) {
    // `timeout` is a network failure with a deadline attached: still nothing
    // was judged, so it must not burn an attempt.
    return err.kind === 'timeout' ? 'network' : err.kind;
  }

  const status: number | undefined = err?.response?.status;
  // No response — the request never landed. Includes axios ECONNABORTED
  // timeouts, which are the same "we never heard back" situation.
  if (typeof status !== 'number') return 'network';

  // 408 Request Timeout and 429 Too Many Requests are the server asking to be
  // tried again, not refusing the work.
  if (status >= 500 || status === 408 || status === 429) return 'server';

  return 'rejected';
}

/**
 * Has this item run out of attempts?
 *
 * `undefined` (a row written before this policy existed) is treated as
 * `rejected`, the strictest bucket — those rows had already been abandoned by
 * the old `retryCount < 3` filter, and the point of this change is that they
 * become visible rather than that they retry more.
 */
export function isExhausted(kind: SyncFailureKind | undefined, retryCount: number): boolean {
  switch (kind) {
    case 'network':
      return false;
    case 'server':
      return retryCount >= MAX_SERVER_ERROR_ATTEMPTS;
    case 'rejected':
    default:
      return retryCount >= MAX_REJECTED_ATTEMPTS;
  }
}

/** A short reason worth putting in front of a user. */
export function describeFailure(err: any): string {
  return (
    err?.response?.data?.error ||
    err?.response?.data?.message ||
    err?.userMessage ||
    err?.message ||
    'Unknown error'
  );
}

/**
 * Fold a failure into an item's retry state.
 *
 * Returns the next status. Mutates nothing — callers own their record shape.
 */
export function nextFailureState(
  kind: SyncFailureKind,
  currentRetryCount: number
): { status: QueueStatus; retryCount: number } {
  if (kind === 'network') {
    // Not an attempt. Straight back to pending, count untouched.
    return { status: 'pending', retryCount: currentRetryCount };
  }
  const retryCount = currentRetryCount + 1;
  return { status: isExhausted(kind, retryCount) ? 'failed' : 'error', retryCount };
}
